<template>
  <div id="document-and-download-button">
    <div id="document-and-metadata">
      <div id="table-container">
        <loading-wrapper
          :is-loading="!file && !digitalizationDocument"
          :positioning="'flex-center'"
        >
          <inspection-due-date-panel
            v-if="mode === modes.project && !isSharePointUploadFailed()"
            :inspection-due-date="file!.inspectionDueDate.toString()"
            :file-id="file!._id.toString()"
            :project-id="projectId"
            @inspection-due-date-updated="$emit('document-data-changed')"
          ></inspection-due-date-panel>

          <vf-button
            v-if="
              mode === modes.project &&
              isUserRole(rerunningFormalControlAllowedRoles) &&
              !isSharePointUploadFailed()
            "
            :busy="isRerunningFormalControl"
            :disabled="isRerunFormalControlButtonDisabled()"
            :variant="rerunFormalControlButtonVariant"
            @click="handleRerunFormalControlButtonClick"
            >{{ $t('documentDetailView.rerunFormalControl') }}</vf-button
          >

          <supplier-document-metadata
            v-if="file && mode === ProjectType.SUPPLIER_PROJECT"
            :document="file"
            :project-id="projectId"
            @document-data-changed="$emit('document-data-changed')"
          >
          </supplier-document-metadata>
          <digitalization-document-metadata
            v-else-if="
              digitalizationDocument &&
              mode === ProjectType.DIGITALIZATION_PROJECT
            "
            :document="digitalizationDocument"
            :project-id="projectId"
            @document-data-changed="$emit('document-data-changed')"
          />
        </loading-wrapper>
      </div>

      <div id="pdf-container">
        <loading-wrapper
          :is-loading="!encodedFile"
          :positioning="'flex-center'"
        >
          <iframe
            v-if="fileForDownload && isPdf"
            :src="fileForDownload"
            width="95%"
            height="860px"
          ></iframe>
        </loading-wrapper>
      </div>
    </div>

    <div class="download-button-container">
      <a
        :download="isPdf ? null : filename"
        :href="fileForDownload!"
        target="_blank"
      >
        <vf-button
          :disabled="!fileForDownload"
          :size="buttonSizes.large"
          :variant="buttonVariants.secondary"
        >
          {{
            isPdf
              ? t('documentDetailView.openInNewTab')
              : t('documentDetailView.downloadDocument')
          }}
          <font-awesome-icon
            v-if="isPdf"
            :icon="['fas', 'arrow-up-right-from-square']"
          />
          <font-awesome-icon v-else :icon="['fas', 'download']" />
        </vf-button>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import { useRoles } from '@/composables/useRoles';
  import axios, { AxiosError } from 'axios';
  import InspectionDueDatePanel from '@/components/InspectionDueDatePanel/InspectionDueDatePanel.vue';
  import VfButton from '@/components/VfButton/VfButton.vue';
  import endpointsList from '../../helpers/endpointsList';
  import { ButtonVariant } from '../VfButton/VfButton.types';
  import {
    IDigitalizationDocumentDetails,
    ISupplierDocumentDetails,
    positiveCheckBotStatuses,
    ProjectType,
    Role,
    Serialized,
    waitingInspectorStatuses,
  } from 'types';
  import { getFileNameFromContentDisposition } from 'utils';
  import { IMetadataEntry } from '@/views/DocumentDetailView/DocumentDetailView.types';
  import SupplierDocumentMetadata from '../DocumentsMetadata/SupplierDocumentMetadata.vue';

  const { t } = useI18n();
  const { createSuccessToast, createErrorToast } = useToast();
  const { isUserRole } = useRoles();

  const emit = defineEmits<{
    (e: 'document-data-changed'): void;
  }>();

  const props = defineProps<{
    mode: ProjectType;
    file?: Serialized<ISupplierDocumentDetails>;
    digitalizationDocument?: Serialized<IDigitalizationDocumentDetails>;
    projectId: string;
    documentId: string;
    uploadId: string;
    metadata: IMetadataEntry[];
  }>();

  const modes = ref({
    project: ProjectType.SUPPLIER_PROJECT,
    digitalizationProject: ProjectType.DIGITALIZATION_PROJECT,
  });
  const encodedFile = ref<string | null>(null);
  const fileForDownload = ref<string | null>(null);
  const pdfDownloadCancelTokenSource = ref(axios.CancelToken.source());
  const rerunningFormalControlAllowedRoles = ref([
    Role.Admin,
    Role.DocuManager,
  ]);
  const rerunFormalControlButtonVariant = ref(ButtonVariant.SECONDARY);
  const isRerunningFormalControl = ref(false);
  const isPdf = ref<boolean | undefined>(undefined);
  const filename = ref<string | undefined>(undefined);

  async function getFileFromSharepoint() {
    if (!(props.file || props.digitalizationDocument)) {
      return;
    }

    try {
      const response = await axios.get<Blob>(
        endpointsList.commonDocuments.streamFile(
          props.file?._id.toString() ??
            props.digitalizationDocument!._id.toString(),
          getProjectTypeForMode(),
        ),
        {
          responseType: 'blob',
          cancelToken: pdfDownloadCancelTokenSource.value.token,
        },
      );

      isPdf.value = response.headers['content-type'] === 'application/pdf';
      filename.value =
        getFileNameFromContentDisposition(
          response.headers['content-disposition'],
        ) ?? undefined;

      const blob = response.data;
      encodeFileForReader(blob);

      fileForDownload.value = URL.createObjectURL(blob);
    } catch (err) {
      if (err instanceof AxiosError && err.code === 'ERR_CANCELED') {
        return;
      }
      createErrorToast(t('documentDetailView.errors.cannotDownloadFile'));
    }
  }

  function getProjectTypeForMode(): ProjectType {
    switch (props.mode) {
      case modes.value.project:
        return ProjectType.SUPPLIER_PROJECT;
      case modes.value.digitalizationProject:
        return ProjectType.DIGITALIZATION_PROJECT;
      default:
        throw new Error('Unknown mode');
    }
  }

  function encodeFileForReader(file: Blob): void {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      encodedFile.value = reader.result as string;
    };
  }

  function isSharePointUploadFailed(): boolean {
    return (
      props.file?.didSharePointUploadFail ||
      props.digitalizationDocument?.didSharePointUploadFail!
    );
  }

  async function handleRerunFormalControlButtonClick() {
    isRerunningFormalControl.value = true;
    try {
      await axios.post(
        endpointsList.metadataCheck.executeMetadataCheck(
          props.file!._id.toString(),
        ),
      );

      emit('document-data-changed');

      createSuccessToast(
        t('documentDetailView.successfullyRerunFormalControl.long'),
      );
    } catch {
      createErrorToast(
        t('documentDetailView.errors.errorRerunningMetadataCheck.long'),
      );
    } finally {
      isRerunningFormalControl.value = false;
    }
  }

  function isRerunFormalControlButtonDisabled(): boolean {
    return (
      !props.file ||
      !waitingInspectorStatuses.includes(props.file?.inspectorStatus) ||
      !positiveCheckBotStatuses.includes(props.file?.checkBotStatus) ||
      props.file?.isReadOnly
    );
  }

  onMounted(() => {
    if (!(props.file || props.digitalizationDocument)) {
      return;
    }

    getFileFromSharepoint();
  });

  onBeforeUnmount(() => {
    pdfDownloadCancelTokenSource.value.cancel('Component destroyed');
  });
</script>

<style lang="scss" scoped>
  #document-and-download-button {
    display: flex;
    flex-direction: column;

    .download-button-container {
      display: flex;
      justify-content: flex-end;

      button {
        margin-top: 20px;
        align-self: flex-end;
        display: flex;
        column-gap: 1rem;
        align-items: center;
      }
    }
  }

  #document-and-metadata {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto;
    border-bottom: 1px solid $border-color;

    #table-container {
      grid-area: 1 / 1 / 2 / 2;
      height: 100%;
      padding: 10px;
      margin: 0;
      border-right: 1px solid $border-color;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      overflow-x: hidden;

      button {
        margin-top: 20px;
        align-self: center;
      }

      h4 {
        margin: 20px 0;
      }
    }

    #pdf-container {
      grid-area: 1 / 2 / 2 / 3;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      row-gap: 30px;
    }
  }
</style>
